import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainOrderItemStatus, DomainOrderStatus } from '../types/models/DomainOrder';
import type { ApiResponseDomainOrderList, ApiResponseDomainOrderSingle } from '../types/api/ApiResponseDomainOrder';

/** A paid registration order whose domains the registry has not confirmed yet. */
export interface InFlightDomainRegistration {
  orderId: number;
  /** The order's domains still `pending` or `initiated`. */
  names: string[];
}

const IN_FLIGHT_ORDER_STATUSES: ReadonlySet<DomainOrderStatus> = new Set<DomainOrderStatus>(['paid', 'fulfilling']);
const IN_FLIGHT_ITEM_STATUSES: ReadonlySet<DomainOrderItemStatus> = new Set<DomainOrderItemStatus>([
  'pending',
  'initiated',
]);

/**
 * The organization's paid registrations that are not in the domain list yet: a registered domain
 * only appears there once the registry confirms it. Reads `GET /datacenters/domain-orders` (summary
 * list), then `GET /datacenters/domain-orders/{id}` for each paid/fulfilling registration (usually
 * 0–1) to get its item names.
 *
 * Read once per mount, never polled. Best effort: a failed list read yields `[]`, a failed order
 * read drops that order, so the caller never shows an error or blocks on it. `apiFetch` is read
 * through a ref so an OIDC silent renew doesn't re-run the read.
 */
export const useInFlightDomainRegistrations = (): InFlightDomainRegistration[] => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [registrations, setRegistrations] = useState<InFlightDomainRegistration[]>([]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    let active = true;
    const controller = new AbortController();

    const readOrder = async (orderId: number): Promise<InFlightDomainRegistration | null> => {
      try {
        const response = await apiFetchRef.current(`/datacenters/domain-orders/${orderId}`, {
          signal: controller.signal,
        });
        const body = (await response.json().catch(() => null)) as ApiResponseDomainOrderSingle | null;
        const order = body?.status === 'success' ? body.data?.domain_order : undefined;
        if (order === undefined) {
          return null;
        }
        const names = order.items.filter((item) => IN_FLIGHT_ITEM_STATUSES.has(item.status)).map((item) => item.name);
        return names.length > 0 ? { orderId, names } : null;
      } catch {
        return null;
      }
    };

    const load = async () => {
      try {
        const response = await apiFetchRef.current('/datacenters/domain-orders', { signal: controller.signal });
        const body = (await response.json().catch(() => null)) as ApiResponseDomainOrderList | null;
        const orders = body?.status === 'success' ? body.data?.domain_orders : undefined;
        if (orders === undefined) {
          return;
        }
        const inFlight = orders.filter(
          (order) => order.type === 'registration' && IN_FLIGHT_ORDER_STATUSES.has(order.status),
        );
        if (inFlight.length === 0) {
          return;
        }
        const read = await Promise.all(inFlight.map((order) => readOrder(order.id)));
        if (active) {
          setRegistrations(
            read.filter((registration): registration is InFlightDomainRegistration => registration !== null),
          );
        }
      } catch {
        // Best effort: an aborted or failed read leaves the list empty.
      }
    };

    void load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [auth.isAuthenticated]);

  return registrations;
};
